import { getGuestGenderByTitle, isGuestAlreadyInArray } from '@utils/helpers';
import { useFormContext } from 'react-hook-form';
import { Guest } from 'shared-types/Basket';
import { GuestsFormValues, MappedGuest } from '../../types';

type GuestFormProps = {
  availableGuests: MappedGuest[];
  basketGuests: Guest[];
};

function findEmptyGuest(
  rooms: GuestsFormValues['rooms']
): { roomNumber: number; guestIndex: number } | null {
  let result: { roomNumber: number; guestIndex: number } | null = null;

  rooms.forEach((room, roomNumber) => {
    room.forEach((guest, guestIndex) => {
      if (!guest.firstName && !guest.lastName && result === null) {
        result = { roomNumber, guestIndex };
      }
    });
  });

  return result;
}

function findMatchingGuest(
  rooms: GuestsFormValues['rooms'],
  existingGuest: MappedGuest
): { roomNumber: number; guestIndex: number } | null {
  let result: { roomNumber: number; guestIndex: number } | null = null;

  rooms.forEach((room, roomNumber) => {
    room.forEach((guest, guestIndex) => {
      if (
        guest.firstName === existingGuest.firstName &&
        guest.lastName === existingGuest.lastName &&
        result === null
      ) {
        result = { roomNumber, guestIndex };
      }
    });
  });

  return result;
}

export function useAddExistingGuest({ availableGuests, basketGuests }: GuestFormProps) {
  const form = useFormContext();
  const { setValue, watch } = form;
  const guestsByRoom = watch('guests.rooms');
  const selectedGuests = (guestsByRoom || []).flat();
  const handleGuestPillClick = ({ guest }) => {
    const { title, firstName, lastName, gender } = guest;
    const mappedGuest = {
      title,
      firstName,
      lastName,
      gender: gender ?? getGuestGenderByTitle(guest),
    };

    if (!isGuestAlreadyInArray(selectedGuests, mappedGuest)) {
      const firstGuestWithNoValue = findEmptyGuest(guestsByRoom);

      if (firstGuestWithNoValue) {
        setValue(
          `guests.rooms.${firstGuestWithNoValue.roomNumber}.${firstGuestWithNoValue.guestIndex}`,
          { ...mappedGuest, roomNumber: firstGuestWithNoValue?.roomNumber ?? 1 },
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
      }
    } else {
      const firstMatchingGuestIndex = findMatchingGuest(guestsByRoom, guest);

      if (firstMatchingGuestIndex) {
        setValue(
          `guests.rooms.${firstMatchingGuestIndex.roomNumber}.${firstMatchingGuestIndex.guestIndex}`,
          { title: '', firstName: '', lastName: '', gender: '', roomNumber: '' },
          { shouldValidate: true, shouldDirty: true }
        );
      }
    }
  };

  const existingGuests = [...availableGuests];
  basketGuests.forEach((guest) => {
    const existingGuest = availableGuests.find(
      (g) => g.firstName === guest.firstName && g.lastName === guest.lastName
    );
    if (!existingGuest) {
      existingGuests.push({
        title: guest.title,
        firstName: guest.firstName,
        lastName: guest.lastName,
        gender: guest.gender,
      });
    }
  });
  return { handleGuestPillClick, selectedGuests, existingGuests };
}
